import { Card, Text, Center, Skeleton } from '@mantine/core';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useContentAnalytics } from '@hooks/useContentAnalytics';
import { useTranslation } from 'react-i18next';
import styles from './Stats.module.scss';

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
  }>;
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ padding: '8px 12px' }}>
        <div style={{ fontSize: '14px' }}>{label}</div>
        <div style={{ fontSize: '16px', fontWeight: 600 }}>
          {payload[0].value} {payload[0].value === 1 ? 'post' : 'posts'}
        </div>
      </div>
    );
  }
  return null;
};

const ChartPlaceholder = () => {
  const { t } = useTranslation();
  return (
    <Center style={{ height: '100%', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} height={20} radius="xl" style={{ maxWidth: `${100 - i * 15}%` }} />
        ))}
      </div>
      <Text size="sm" c="dimmed">
        {t('common.noData')}
      </Text>
    </Center>
  );
};

export const ContentTimeline = () => {
  const { t } = useTranslation();
  const { timelineCounts } = useContentAnalytics();
  const data = Object.entries(timelineCounts).map(([date, value]) => ({ date, value }));

  const hasData = data.length > 0;

  if (!hasData) return null;

  return (
    <Card withBorder className={styles.stats__card}>
      <div className={styles.stats__label}>{t('stats.byDate')}</div>
      <div className={styles.stats__chart}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} tickMargin={8} />
              <YAxis tick={{ fontSize: 12 }} tickMargin={8} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill="#339af0" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ChartPlaceholder />
        )}
      </div>
    </Card>
  );
};
